import api from '@/lib/api'
import { exhaustiveRecord } from '@/lib/typeUtils'

// ============================================================================
// Types
// ============================================================================

export interface AdminStats {
  total_tenants: number
  total_users: number
  verified_users: number
  new_tenants_this_week: number
  new_users_this_week: number
  audit_events_this_week: number
  timestamp: string
}

export interface AdminTenant {
  id: string
  name: string
  slug: string
  plan: string
  is_active: boolean
  is_deleted: boolean
  suspended_at: string | null
  created_at: string
  user_count: number
  predictions_this_month: number
  max_predictions_month: number
}

export interface AdminTenantUser {
  id: string
  email: string
  name: string
  role: string
  email_verified: boolean
  is_active: boolean
  is_locked: boolean
  created_at: string
  last_login: string | null
}

export interface AdminTenantDetail {
  id: string
  name: string
  slug: string
  plan: string
  is_active: boolean
  is_deleted: boolean
  created_at: string
  updated_at: string
  // Limits
  max_predictions_month: number
  max_data_sources: number
  max_users: number
  max_models: number
  // Usage
  predictions_this_month: number
  data_sources_count: number
  users_count: number
  models_count: number
  // Suspension info
  suspended_at: string | null
  suspended_by_id: string | null
  suspension_reason: string | null
  // Deletion info
  deleted_at: string | null
  deleted_by_id: string | null
  // Users
  users: AdminTenantUser[]
  settings: Record<string, unknown>
}

export interface AdminUser {
  id: string
  email: string
  name: string
  role: string
  is_active: boolean
  is_locked: boolean
  email_verified: boolean
  tenant_id: string | null
  tenant_name: string | null
  created_at: string
  last_login: string | null
}

export interface AdminPlan {
  id: string
  name: string
  price: number
  predictions_limit: number | string
  models_limit: number | string
  data_sources_limit: number | string
  users_limit: number
  features: string[]
  tenant_count: number
}

export interface AuditLogEntry {
  id: string
  user_id: string | null
  tenant_id: string | null
  action: string
  resource_type: string | null
  resource_id: string | null
  ip_address: string | null
  details: Record<string, unknown>
  acting_as_tenant_id: string | null
  created_at: string
}

// Request types
export interface CreateTenantRequest {
  name: string
  slug?: string
  plan: 'free' | 'starter' | 'growth' | 'scale'
  owner_email: string
  owner_name: string
  owner_password?: string
  send_welcome_email?: boolean
}

export interface UpdateTenantRequest {
  name?: string
  slug?: string
}

export interface SuspendTenantRequest {
  reason: string
  notify_users?: boolean
}

export interface ChangePlanRequest {
  plan: 'free' | 'starter' | 'growth' | 'scale'
  reason?: string
  notify_tenant?: boolean
}

export interface LockUserRequest {
  reason: string
  notify_user?: boolean
}

export interface ResetPasswordRequest {
  send_reset_email?: boolean
  generate_temporary?: boolean
}

export interface ChangeRoleRequest {
  role: 'owner' | 'admin' | 'member' | 'viewer'
  reason?: string
}

// Response types
export interface TenantListResponse {
  tenants: AdminTenant[]
  total: number
  page: number
  page_size: number
}

export interface UserListResponse {
  users: AdminUser[]
  total: number
  page: number
  page_size: number
}

export interface AuditLogResponse {
  logs: AuditLogEntry[]
  total: number
  page: number
  page_size: number
}

export interface TenantCreatedResponse {
  id: string
  name: string
  slug: string
  plan: string
  owner_id: string
  owner_email: string
  temporary_password?: string
}

export interface ResetPasswordResponse {
  success: boolean
  message: string
  temporary_password?: string
}

// Query params
export interface TenantListParams {
  page?: number
  page_size?: number
  search?: string
  plan?: string
  is_active?: boolean
  is_deleted?: boolean
  sort_by?: string
  sort_order?: 'asc' | 'desc'
}

export interface UserListParams {
  page?: number
  page_size?: number
  search?: string
  tenant_id?: string
  role?: string
  is_active?: boolean
  is_locked?: boolean
}

export interface AuditLogParams {
  page?: number
  page_size?: number
  user_id?: string
  tenant_id?: string
  action?: string
  start_date?: string
  end_date?: string
}

// ============================================================================
// Service
// ============================================================================

export const adminService = {
  // Stats
  async getStats(): Promise<AdminStats> {
    const { data } = await api.get<AdminStats>('/admin/stats')
    return data
  },

  // Tenants - List & Detail
  async listTenants(params: TenantListParams = {}): Promise<TenantListResponse> {
    const { data } = await api.get<TenantListResponse>('/admin/tenants/enhanced', {
      params,
    })
    return data
  },

  async getTenant(id: string): Promise<AdminTenantDetail> {
    const { data } = await api.get<AdminTenantDetail>(`/admin/tenants/${id}/enhanced`)
    return data
  },

  // Tenants - CRUD
  async createTenant(request: CreateTenantRequest): Promise<TenantCreatedResponse> {
    const { data } = await api.post<TenantCreatedResponse>('/admin/tenants', request)
    return data
  },

  async updateTenant(id: string, request: UpdateTenantRequest): Promise<{ id: string; name: string; slug: string; updated: boolean }> {
    const { data } = await api.patch<{ id: string; name: string; slug: string; updated: boolean }>(
      `/admin/tenants/${id}`,
      request
    )
    return data
  },

  async deleteTenant(id: string, hardDelete = false, confirm?: string): Promise<{ deleted: boolean; hard_delete: boolean; tenant_id: string }> {
    const params: Record<string, unknown> = { hard_delete: hardDelete }
    if (confirm) params.confirm = confirm
    const { data } = await api.delete<{ deleted: boolean; hard_delete: boolean; tenant_id: string }>(
      `/admin/tenants/${id}`,
      { params }
    )
    return data
  },

  // Tenants - Suspend/Reactivate
  async suspendTenant(id: string, request: SuspendTenantRequest): Promise<{ suspended: boolean; tenant_id: string; suspended_at: string }> {
    const { data } = await api.post<{ suspended: boolean; tenant_id: string; suspended_at: string }>(
      `/admin/tenants/${id}/suspend`,
      request
    )
    return data
  },

  async reactivateTenant(id: string): Promise<{ reactivated: boolean; tenant_id: string }> {
    const { data } = await api.post<{ reactivated: boolean; tenant_id: string }>(
      `/admin/tenants/${id}/reactivate`
    )
    return data
  },

  // Tenants - Plan
  async changeTenantPlan(id: string, request: ChangePlanRequest): Promise<{ success: boolean; tenant_id: string; old_plan: string; new_plan: string; new_limits: Record<string, number> }> {
    const { data } = await api.post<{ success: boolean; tenant_id: string; old_plan: string; new_plan: string; new_limits: Record<string, number> }>(
      `/admin/tenants/${id}/plan`,
      request
    )
    return data
  },

  // Users
  async listUsers(params: UserListParams = {}): Promise<UserListResponse> {
    const { data } = await api.get<UserListResponse>('/admin/users', { params })
    return data
  },

  async lockUser(userId: string, request: LockUserRequest): Promise<{ locked: boolean; user_id: string; locked_at: string }> {
    const { data } = await api.post<{ locked: boolean; user_id: string; locked_at: string }>(
      `/admin/users/${userId}/lock`,
      request
    )
    return data
  },

  async unlockUser(userId: string): Promise<{ unlocked: boolean; user_id: string }> {
    const { data } = await api.post<{ unlocked: boolean; user_id: string }>(
      `/admin/users/${userId}/unlock`
    )
    return data
  },

  async resetUserPassword(userId: string, request: ResetPasswordRequest = {}): Promise<ResetPasswordResponse> {
    const { data } = await api.post<ResetPasswordResponse>(
      `/admin/users/${userId}/reset-password`,
      request
    )
    return data
  },

  async changeUserRole(userId: string, request: ChangeRoleRequest): Promise<{ success: boolean; user_id: string; old_role: string; new_role: string }> {
    const { data } = await api.patch<{ success: boolean; user_id: string; old_role: string; new_role: string }>(
      `/admin/users/${userId}/role`,
      request
    )
    return data
  },

  // Plans
  async listPlans(): Promise<{ plans: AdminPlan[] }> {
    const { data } = await api.get<{ plans: AdminPlan[] }>('/admin/plans')
    return data
  },

  // Audit Logs
  async getAuditLogs(params: AuditLogParams = {}): Promise<AuditLogResponse> {
    const { data } = await api.get<AuditLogResponse>('/admin/audit-logs', { params })
    return data
  },

  // Impersonation
  async startImpersonation(tenantId: string): Promise<{ impersonation_token: string; tenant_id: string; tenant_name: string; expires_at: string }> {
    const { data } = await api.post<{ impersonation_token: string; tenant_id: string; tenant_name: string; expires_at: string }>(
      `/admin/impersonate/${tenantId}`
    )
    return data
  },

  async endImpersonation(): Promise<void> {
    await api.delete('/admin/impersonate')
  },
}

// Plan utilities
export const PLAN_ORDER = ['free', 'starter', 'growth', 'scale'] as const
export type PlanId = typeof PLAN_ORDER[number]

export const PLAN_COLORS = exhaustiveRecord<PlanId, string>({
  free: 'bg-gray-100 text-gray-800',
  starter: 'bg-blue-100 text-blue-800',
  growth: 'bg-purple-100 text-purple-800',
  scale: 'bg-amber-100 text-amber-800',
})

export const PLAN_LABELS = exhaustiveRecord<PlanId, string>({
  free: 'Free',
  starter: 'Starter',
  growth: 'Growth',
  scale: 'Scale',
})

// Status utilities
export type TenantStatus = 'active' | 'suspended' | 'deleted'

export function getTenantStatus(tenant: { is_active: boolean; is_deleted: boolean }): TenantStatus {
  if (tenant.is_deleted) return 'deleted'
  if (!tenant.is_active) return 'suspended'
  return 'active'
}

export const TENANT_STATUS_COLORS = exhaustiveRecord<TenantStatus, string>({
  active: 'bg-green-100 text-green-800',
  suspended: 'bg-yellow-100 text-yellow-800',
  deleted: 'bg-red-100 text-red-800',
})

export const TENANT_STATUS_LABELS = exhaustiveRecord<TenantStatus, string>({
  active: 'Active',
  suspended: 'Suspended',
  deleted: 'Deleted',
})

export type UserStatus = 'active' | 'locked' | 'inactive'

export function getUserStatus(user: { is_active: boolean; is_locked: boolean }): UserStatus {
  if (user.is_locked) return 'locked'
  if (!user.is_active) return 'inactive'
  return 'active'
}

export const USER_STATUS_COLORS = exhaustiveRecord<UserStatus, string>({
  active: 'bg-green-100 text-green-800',
  locked: 'bg-red-100 text-red-800',
  inactive: 'bg-gray-100 text-gray-800',
})

export const USER_STATUS_LABELS = exhaustiveRecord<UserStatus, string>({
  active: 'Active',
  locked: 'Locked',
  inactive: 'Inactive',
})

// ============================================================================
// Feedback Types
// ============================================================================

export type FeedbackStatusType = 'submitted' | 'in_review' | 'approved' | 'in_progress' | 'testing' | 'confirmed' | 'closed'
export type FeedbackPriorityType = 'low' | 'medium' | 'high' | 'critical'
export type FeedbackTypeType = 'bug' | 'feature' | 'improvement' | 'question'

export interface FeedbackAttachment {
  id: string
  type: 'image' | 'pdf' | 'loom' | 'text'
  filename: string
  object_key?: string
  content_type?: string
  size_bytes?: number
  loom_url?: string
  uploaded_at: string
}

export interface FeedbackItem {
  id: string
  title: string
  description: string
  status: FeedbackStatusType
  priority: FeedbackPriorityType
  feedback_type: FeedbackTypeType
  created_by_id: string | null
  created_by_name: string | null
  assigned_to_id: string | null
  assigned_to_name: string | null
  attachments: FeedbackAttachment[]
  implementation_plan: string | null
  plan_approved_at: string | null
  implementation_notes: string | null
  tags: string[]
  github_issue_url: string | null
  created_at: string
  updated_at: string
  confirmed_at: string | null
  closed_at: string | null
}

export interface FeedbackComment {
  id: string
  feedback_id: string
  user_id: string
  user_name: string
  content: string
  is_status_change: boolean
  old_status: string | null
  new_status: string | null
  created_at: string
}

export interface FeedbackListParams {
  page?: number
  page_size?: number
  status?: FeedbackStatusType
  priority?: FeedbackPriorityType
  feedback_type?: FeedbackTypeType
  search?: string
}

export interface FeedbackListResponse {
  items: FeedbackItem[]
  total: number
  page: number
  page_size: number
}

export interface CreateFeedbackRequest {
  title: string
  description: string
  priority?: FeedbackPriorityType
  feedback_type?: FeedbackTypeType
  tags?: string[]
}

export interface UpdateFeedbackRequest {
  title?: string
  description?: string
  priority?: FeedbackPriorityType
  feedback_type?: FeedbackTypeType
  tags?: string[]
  assigned_to_id?: string
  implementation_plan?: string
  implementation_notes?: string
  github_issue_url?: string
}

export interface FeedbackPresignResponse {
  url: string
  method: string
  headers: Record<string, string>
  attachment_id: string
  object_key: string
  expires_at: string
}

export interface CopyForClaudeResponse {
  markdown: string
  feedback_id: string
  title: string
}

// Feedback status utilities
export const FEEDBACK_STATUS_ORDER: FeedbackStatusType[] = [
  'submitted', 'in_review', 'approved', 'in_progress', 'testing', 'confirmed', 'closed'
]

export const FEEDBACK_STATUS_COLORS = exhaustiveRecord<FeedbackStatusType, string>({
  submitted: 'bg-blue-100 text-blue-800',
  in_review: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-purple-100 text-purple-800',
  in_progress: 'bg-orange-100 text-orange-800',
  testing: 'bg-cyan-100 text-cyan-800',
  confirmed: 'bg-green-100 text-green-800',
  closed: 'bg-gray-100 text-gray-800',
})

export const FEEDBACK_STATUS_LABELS = exhaustiveRecord<FeedbackStatusType, string>({
  submitted: 'Submitted',
  in_review: 'In Review',
  approved: 'Approved',
  in_progress: 'In Progress',
  testing: 'Testing',
  confirmed: 'Confirmed',
  closed: 'Closed',
})

export const FEEDBACK_PRIORITY_COLORS = exhaustiveRecord<FeedbackPriorityType, string>({
  low: 'bg-gray-100 text-gray-800',
  medium: 'bg-blue-100 text-blue-800',
  high: 'bg-orange-100 text-orange-800',
  critical: 'bg-red-100 text-red-800',
})

export const FEEDBACK_PRIORITY_LABELS = exhaustiveRecord<FeedbackPriorityType, string>({
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
})

export const FEEDBACK_TYPE_COLORS = exhaustiveRecord<FeedbackTypeType, string>({
  bug: 'bg-red-100 text-red-800',
  feature: 'bg-green-100 text-green-800',
  improvement: 'bg-blue-100 text-blue-800',
  question: 'bg-purple-100 text-purple-800',
})

export const FEEDBACK_TYPE_LABELS = exhaustiveRecord<FeedbackTypeType, string>({
  bug: 'Bug',
  feature: 'Feature',
  improvement: 'Improvement',
  question: 'Question',
})

// ============================================================================
// Feedback Service
// ============================================================================

export const feedbackService = {
  async getFeedbackCount(): Promise<{ pending_count: number }> {
    const { data } = await api.get<{ pending_count: number }>('/admin/feedback/count')
    return data
  },

  async listFeedback(params: FeedbackListParams = {}): Promise<FeedbackListResponse> {
    const { data } = await api.get<FeedbackListResponse>('/admin/feedback', { params })
    return data
  },

  async getFeedback(id: string): Promise<FeedbackItem> {
    const { data } = await api.get<FeedbackItem>(`/admin/feedback/${id}`)
    return data
  },

  async createFeedback(request: CreateFeedbackRequest): Promise<FeedbackItem> {
    const { data } = await api.post<FeedbackItem>('/admin/feedback', request)
    return data
  },

  async updateFeedback(id: string, request: UpdateFeedbackRequest): Promise<FeedbackItem> {
    const { data } = await api.patch<FeedbackItem>(`/admin/feedback/${id}`, request)
    return data
  },

  async deleteFeedback(id: string): Promise<void> {
    await api.delete(`/admin/feedback/${id}`)
  },

  async transitionStatus(id: string, newStatus: FeedbackStatusType, comment?: string): Promise<FeedbackItem> {
    const { data } = await api.post<FeedbackItem>(`/admin/feedback/${id}/transition`, {
      new_status: newStatus,
      comment,
    })
    return data
  },

  async approvePlan(id: string): Promise<FeedbackItem> {
    const { data } = await api.post<FeedbackItem>(`/admin/feedback/${id}/approve-plan`)
    return data
  },

  async getComments(id: string): Promise<FeedbackComment[]> {
    const { data } = await api.get<FeedbackComment[]>(`/admin/feedback/${id}/comments`)
    return data
  },

  async addComment(id: string, content: string): Promise<FeedbackComment> {
    const { data } = await api.post<FeedbackComment>(`/admin/feedback/${id}/comments`, { content })
    return data
  },

  async presignAttachment(
    id: string,
    filename: string,
    contentType: string,
    fileSize: number
  ): Promise<FeedbackPresignResponse> {
    const { data } = await api.post<FeedbackPresignResponse>(
      `/admin/feedback/${id}/attachments/presign`,
      null,
      { params: { filename, content_type: contentType, file_size: fileSize } }
    )
    return data
  },

  async addLoomAttachment(id: string, loomUrl: string): Promise<{ success: boolean; attachment_id: string }> {
    const { data } = await api.post<{ success: boolean; attachment_id: string }>(
      `/admin/feedback/${id}/attachments/loom`,
      null,
      { params: { loom_url: loomUrl } }
    )
    return data
  },

  async deleteAttachment(feedbackId: string, attachmentId: string): Promise<void> {
    await api.delete(`/admin/feedback/${feedbackId}/attachments/${attachmentId}`)
  },

  async getCopyForClaude(id: string): Promise<CopyForClaudeResponse> {
    const { data } = await api.get<CopyForClaudeResponse>(`/admin/feedback/${id}/copy-for-claude`)
    return data
  },
}
